// src/components/shipment/AIInsightsModal.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, Brain, Sparkles, Ship, Globe, ArrowRight, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';

const AIInsightsModal = ({ isOpen, onClose, stats, shipments, insightCards }) => {
    if (!isOpen) return null;

    const topCarriers = shipments.reduce((acc, s) => {
        if (s.carrier) {
            acc[s.carrier] = (acc[s.carrier] || 0) + 1;
        }
        return acc;
    }, {});

    const topRoutes = shipments.slice(0, 5).map(s => ({
        from: s.pol?.split(',')[0] || 'N/A',
        to: s.pod?.split(',')[0] || 'N/A',
        count: 1
    }));

    const statCards = [
    {
      bg: "bg-blue-50 dark:bg-blue-500/10",
      text: "text-blue-600 dark:text-blue-400",
      subText: "text-blue-600/70 dark:text-blue-400/70",
    },
    {
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
      text: "text-emerald-600 dark:text-emerald-400",
      subText: "text-emerald-600/70 dark:text-emerald-400/70",
    },
    {
      bg: "bg-amber-50 dark:bg-amber-500/10",
      text: "text-amber-600 dark:text-amber-400",
      subText: "text-amber-600/70 dark:text-amber-400/70",
    },
    {
      bg: "bg-red-50 dark:bg-red-500/10",
      text: "text-red-600 dark:text-red-400",
      subText: "text-red-600/70 dark:text-red-400/70",
    },
  ];
  
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                <Brain className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">AI Analytics Dashboard</h2>
                                <p className="text-purple-100 text-sm">Intelligent insights for your shipments</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                            <XCircle className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        {insightCards &&
                            (insightCards || []).map((item, index) => {
                                return (
                                    <div
                                        key={item.label}
                                        className={`${statCards?.[index]?.bg} rounded-xl p-4 text-center`}
                                    >
                                        <p
                                            className={`text-2xl font-bold ${statCards?.[index]?.text}`}
                                        >
                                            {item.value}
                                        </p>
                                        <p
                                            className={`text-xs font-medium ${statCards?.[index]?.subText}`}
                                        >
                                            {item.title}
                                        </p>
                                    </div>
                                );
                            })}
                    </div>

                    {/* AI Predictions */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-500/10 dark:to-pink-500/10 rounded-xl p-5 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            <h3 className="font-bold text-slate-800 dark:text-white">
                                AI Predictions
                            </h3>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
                                        On-Time Delivery Prediction
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        85% of current shipments are expected to arrive on schedule
                                        based on historical carrier performance.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <AlertCircle className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
                                        Potential Delays
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        2 shipments via MAERSK carrier may experience delays due to
                                        port congestion at Rotterdam.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <TrendingUp className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
                                        Volume Trend
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Shipment volume increased by 12% compared to last month.
                                        Peak season approaching.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Carriers */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Ship className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                <h3 className="font-bold text-slate-800 dark:text-white">
                                    Top Carriers
                                </h3>
                            </div>
                            <div className="space-y-3">
                                {Object.entries(topCarriers)
                                    .slice(0, 5)
                                    .map(([carrier, count], i) => (
                                        <div
                                            key={carrier}
                                            className="flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">
                                                    {i + 1}
                                                </span>
                                                <span className="text-sm text-slate-700 dark:text-slate-200">
                                                    {carrier}
                                                </span>
                                            </div>
                                            <span className="text-sm font-bold text-slate-800 dark:text-white">
                                                {count}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                <h3 className="font-bold text-slate-800 dark:text-white">
                                    Popular Routes
                                </h3>
                            </div>
                            <div className="space-y-3">
                                {topRoutes.map((route, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                            {i + 1}
                                        </span>
                                        <span className="text-xs text-slate-700 dark:text-slate-200">
                                            {route.from}
                                        </span>
                                        <ArrowRight className="w-3 h-3 text-slate-400" />
                                        <span className="text-xs text-slate-700 dark:text-slate-200">
                                            {route.to}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recommendations */}
                    <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5" />
                            <h3 className="font-bold">AI Recommendations</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-blue-100">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-300">•</span>
                                Consider consolidating shipments to Rotterdam to optimize costs
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-300">•</span>
                                Review MAERSK carrier agreements for better rates on US-EU
                                routes
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-300">•</span>
                                Schedule more shipments mid-month to avoid peak congestion
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Last updated: Just now
                        </p>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default AIInsightsModal;